import React, { useState, useRef, useMemo } from "react";
import {
  AllTimeCreativeLeaderboardData,
  CreativeOverviewRow,
} from "@/screens/creatives/types";
import {
  IconArrowDown,
  IconArrowUp,
  IconMovie,
  IconPhoto,
  IconTrophy,
} from "@tabler/icons-react";
import OverviewSectionWrapper from "../section/OverviewSectionWrapper";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getFirstMedia } from "../../CreativeReportGroupCards";
import { IconCards } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import SelectDropdown from "@/components/common/dropdown/SelectDropdown";
import { motion, AnimatePresence } from "framer-motion";
import { getVisiblePages } from "@/components/ui/data-table-pagination2";
import { RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import CreativeDetailsDialog from "../creatives-overview/CreativeDetailsDialog";
import { cn } from "@/lib/utils";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import ErrorState from "@/components/common/errorStates/ErrorState";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { SqButton } from "@/components/squircle-ui/button";
import { RiAddLargeLine } from "@remixicon/react";
import { CSSProperties } from "react";
import { CreativeReportMetadata } from "@/types";
import EditMetricsToggle from "@/components/common/metrics/components/EditMetricsToggle";
import Metrics from "@/components/common/metrics";
import { useGlobalStore } from "@/GlobalStore";

interface AllTimeCreativeLeaderboardProps {
  data?: AllTimeCreativeLeaderboardData;
  title: string;
  loading?: boolean;
  error?: { hasError: boolean; message?: string };
  defaultMetricId?: string;
  onRetry?: () => void;
  onMetricChange?: (metricIds: string[]) => void;
  reportMetadata?: CreativeReportMetadata;
}

const mediaIcons = {
  mixed: IconPhoto,
  video: IconMovie,
  image: IconPhoto,
  "N/A": IconPhoto,
  "": IconPhoto,
};

const AllTimeCreativeLeaderboard: React.FC<AllTimeCreativeLeaderboardProps> = ({
  data,
  title,
  loading = true,
  error,
  defaultMetricId,
  onRetry,
  onMetricChange,
  reportMetadata,
}) => {
  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);
  const metricColumns = data?.creative_metric_columns;
  const allMetricOptions =
    dataConfigurations?.column_choices?.find((c) => c.key === "metric")?.options ?? [];

  // Filter metricOptions to only include metrics that are in metricColumns
  const metricOptions =
    Array.isArray(metricColumns) && Array.isArray(allMetricOptions)
      ? allMetricOptions.filter((option) =>
          metricColumns.some((col) => col.key === option.key),
        )
      : [];

  // Get selected metrics from filtered metricOptions based on data.columns order
  const selectedMetrics =
    data?.columns && Array.isArray(metricOptions)
      ? data.columns
          .map((col) => metricOptions.find((m) => m.key === col.key))
          .filter((m): m is (typeof metricOptions)[number] => !!m)
      : [];

  const [selectedCreative, setSelectedCreative] = useState<CreativeOverviewRow | null>(
    null,
  );
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const handleShowClick = (creative: CreativeOverviewRow) => {
    setSelectedCreative(creative);
    setShowDetailsDialog(true);
  };

  const getMediaTypeIcon = (mediaType: string) => {
    return mediaIcons[mediaType as keyof typeof mediaIcons] || IconPhoto;
  };

  // Helper function to render cell value based on column type
  const renderCellValue = (
    row: AllTimeCreativeLeaderboardData["rows"][0],
    column: AllTimeCreativeLeaderboardData["columns"][0],
  ) => {
    const value = row[column.key];

    if (value === null || value === undefined || value === "N/A") {
      return "—";
    }

    // Handle numeric values with formatting
    if (typeof value === "number") {
      const formattedValue = value.toLocaleString();
      return `${column.prefix || ""}${formattedValue}${column.suffix || ""}`;
    }

    // Handle string values
    if (typeof value === "string") {
      return `${column.prefix || ""}${value}${column.suffix || ""}`;
    }

    // Fallback
    return JSON.stringify(value);
  };

  // Helper function to determine text alignment based on column type
  const getTextAlignment = (column: AllTimeCreativeLeaderboardData["columns"][0]) => {
    const key = column.key.toLowerCase();
    if (key === "current_rank" || key === "creative_name") {
      return "text-left";
    }
    // Check for metric columns that should be right-aligned
    if (column.type === "number") {
      return "text-right";
    }

    // Check for text columns that should be left-aligned
    if (column.type === "text") {
      return "text-left";
    }

    // Default to left alignment
    return "text-left";
  };

  // Get pinned columns (primary columns)
  const pinnedColumns = useMemo(() => {
    return data?.columns.filter((col) => col.is_primary === true) || [];
  }, [data?.columns]);

  // Helper function to get pinned column style
  const getPinnedColumnStyle = (
    column: AllTimeCreativeLeaderboardData["columns"][0],
    isHeader: boolean = false,
  ): CSSProperties => {
    const isPinned = column.is_primary === true;

    if (!isPinned) {
      return {};
    }

    // Calculate left position based on sum of previous pinned columns' widths
    const leftOffset = pinnedColumns
      .slice(
        0,
        pinnedColumns.findIndex((col) => col.key === column.key),
      )
      .reduce((sum, col) => {
        // Use different widths based on column type
        if (col.key === "current_rank") return sum + 100;
        if (col.key === "creative_name") return sum + 400;
        return sum + 147; // Default width
      }, 0);

    return {
      position: "sticky",
      left: `${leftOffset}px`,
      zIndex: isHeader ? 20 : 10,
      backgroundColor: "#ffffff",
    };
  };

  // Helper function to get pinned column classes
  const getPinnedColumnClasses = (
    column: AllTimeCreativeLeaderboardData["columns"][0],
  ): string => {
    const isPinned = column.is_primary === true;
    if (!isPinned) return "";

    const isLastPinned = pinnedColumns[pinnedColumns.length - 1]?.key === column.key;
    return `pinned-cell ${isLastPinned ? "pinned-last" : ""}`;
  };

  // Helper function to get column width
  const getColumnWidth = (
    column: AllTimeCreativeLeaderboardData["columns"][0],
  ): string => {
    if (column.key === "current_rank") return "100px";
    if (column.key === "creative_name") return "400px";
    // For metric columns, calculate width based on header text length
    const headerText = column.label || "";
    const estimatedWidth = Math.max(120, headerText.length * 8) + 40; // 8px per char + padding
    return `${estimatedWidth}px`;
  };

  // Custom render function for rank column
  const renderRankCell = (
    row: AllTimeCreativeLeaderboardData["rows"][0],
    rowIndex: number,
  ) => {
    const rank =
      row.current_rank === "N/A"
        ? "—"
        : row.current_rank || row.all_time_rank || rowIndex + 1;
    const rankChange = row.rank_change || 0;

    return (
      <div className="px-[20px] py-2 text-[12px] text-brand-primary-new">
        <div className="flex items-center gap-2">
          <span className="font-medium">{rank}</span>
          {rankChange === "__LOADING__" ? (
            <div className="flex items-center gap-[2px]">
              <Skeleton className="w-[30px] h-[20px]" />
            </div>
          ) : (
            rankChange !== 0 && (
              <div
                className={cn(
                  "flex items-center gap-[2px]",
                  rankChange > 0 ? "text-[#009B4D]" : "text-[#E81616]",
                )}
              >
                <span
                  className={cn(
                    "-mt-[1px]",
                    rankChange > 0 ? "text-[#009B4D]" : "text-[#E81616]",
                  )}
                >
                  {rankChange > 0 ? (
                    <IconArrowUp size={12} />
                  ) : (
                    <IconArrowDown size={12} />
                  )}
                </span>
                <span className="mt-[1px] text-xs font-medium">
                  {Math.abs(rankChange)}
                </span>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  // Custom render function for creative/messaging column
  const renderCreativeCell = (row: AllTimeCreativeLeaderboardData["rows"][0]) => {
    const firstAd = row.ads?.[0] ?? row.first_ad_metadata;
    const mediaType = row.creative_type || "image";
    const mediaItem =
      (firstAd ? getFirstMedia(firstAd)[0] : undefined) ??
      (row.creative_thumbnail
        ? {
            type: row.creative_type || ("image" as const),
            url: row.creative_thumbnail,
            thumbnail: row.creative_type === "video" ? row.creative_thumbnail : undefined,
          }
        : undefined);
    const totalAds =
      (row as Record<string, unknown>).total_ads != null
        ? Number((row as Record<string, unknown>).total_ads)
        : row.ads?.length || 0;

    return (
      <div className="px-[12px] py-2">
        <div className="flex gap-3 items-center">
          {/* Creative Thumbnail */}
          <div className="relative group cursor-pointer">
            <div className="flex items-center bg-[#EDEFF3] justify-center min-w-[60px] w-[60px] h-[60px] overflow-hidden rounded-[8px]">
              {mediaItem && mediaItem.url ? (
                mediaItem.type === "video" ? (
                  <img
                    src={mediaItem.thumbnail || mediaItem.url}
                    alt="Video thumbnail"
                    loading="lazy"
                    className="w-full h-full object-cover rounded-[8px]"
                  />
                ) : (
                  <img
                    src={mediaItem.url}
                    alt="Creative image"
                    loading="lazy"
                    className="w-full h-full object-cover rounded-[8px]"
                  />
                )
              ) : (
                <div className="w-full h-full squircle-mask squircle-radius-[8px] bg-[#EDEFF3] flex items-center justify-center">
                  {React.createElement(getMediaTypeIcon(mediaType), {
                    className: "h-9 w-9",
                  })}
                </div>
              )}
            </div>

            {/* Hover Overlay */}
            <div
              onClick={() => handleShowClick(row)}
              className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-[8px]"
            >
              <IconCards size={24} className="text-white" />
            </div>

            {/* Media Type Indicator */}
            <div className="absolute bottom-1 right-1 group-hover:opacity-0">
              <div
                className={cn(
                  "w-[20px] h-[20px] squircle-mask squircle-radius-[4px] flex items-center justify-center",
                  mediaType === "video" ? "bg-[#018C62B2]" : "bg-[#FF4699B2]",
                )}
              >
                {React.createElement(getMediaTypeIcon(mediaType), {
                  className: "h-[14px] w-[14px] text-white",
                })}
              </div>
            </div>
          </div>

          {/* Creative Details */}
          <div className="overflow-hidden flex flex-col gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="max-w-min whitespace-nowrap text-[12px] font-medium truncate text-left text-brand-primary-800 leading-5">
                  {row.creative_name || "Untitled Creative"}
                </p>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                className="max-w-[400px] text-wrap"
              >
                {row.creative_name || "Untitled Creative"}
              </TooltipContent>
            </Tooltip>

            <div className="w-full flex justify-between items-center gap-2">
              <div className="flex gap-2 items-center">
                {/* Show Button with Ad Count */}
                {totalAds > 0 && (
                  <Button
                    variant="sq-outline"
                    size="sm"
                    onClick={() => handleShowClick(row)}
                    className=""
                  >
                    <IconCards size={14} className="text-brand-primary-400" />
                    <span className="mt-[2px]">Show</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Function to render cell based on column type
  const renderCell = (
    row: AllTimeCreativeLeaderboardData["rows"][0],
    column: AllTimeCreativeLeaderboardData["columns"][0],
    rowIndex: number,
  ): React.ReactNode => {
    // Special handling for specific column types
    if (column.key === "creative_name" || column.key === "messaging") {
      return renderCreativeCell(row);
    }

    if (
      column.key === "rank" ||
      column.key === "all_time_rank" ||
      column.key === "current_rank"
    ) {
      return renderRankCell(row, rowIndex);
    }

    const textAlign = getTextAlignment(column);

    const isPinned = column.is_primary === true;

    return (
      <Tooltip>
        <TooltipTrigger>
          <div
            className={cn(
              "px-[20px] py-2 text-[12px] text-brand-primary-new",
              textAlign,
              isPinned && "overflow-hidden",
            )}
          >
            {renderCellValue(row, column)}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="center">
          {renderCellValue(row, column)}
        </TooltipContent>
      </Tooltip>
    );
  };

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const isUserInitiatedPaginationChanges = useRef<boolean>(false);

  // Calculate pagination
  const totalRows = data?.rows.length || 0;
  const totalPages = Math.ceil(totalRows / pagination.pageSize);
  const startIndex = pagination.pageIndex * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const currentPageRows = data?.rows.slice(startIndex, endIndex) || [];

  // Pagination handlers
  const handlePageChange = (newPageIndex: number) => {
    isUserInitiatedPaginationChanges.current = true;
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    isUserInitiatedPaginationChanges.current = true;
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  // Page size options
  const pageSizeOptions = [10, 30, 50, 100];

  // metrics state
  const [showAddMetric, setShowAddMetric] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrolled = useRef<boolean>(false);

  const handleMetricsChange = (metrics: typeof metricOptions) => {
    const previousMetricsCount = selectedMetrics?.length || 0;
    const metricIds = metrics.map((m) => m.key);
    onMetricChange?.(metricIds);
    // Scroll to right if metrics were added from table header
    if (metrics.length > previousMetricsCount) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            left: scrollContainerRef.current.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  // Handle scroll to add shadow to pinned columns
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    const wasScrolled = isScrolled.current;
    isScrolled.current = scrollLeft > 0;

    if (wasScrolled !== isScrolled.current) {
      const pinnedCells = e.currentTarget.querySelectorAll(".pinned-last");
      pinnedCells.forEach((cell) => {
        cell.classList.toggle("pinned-shadow", isScrolled.current);
      });
    }
  };

  return (
    <>
      <style>
        {`
        .pinned-shadow {
          box-shadow: 8px 0 8px -4px rgba(228, 228, 231, 0.40);
        }
        .pinned-last {
          border-right: 1px solid #e5e7eb;
        }
        .pinned-cell {
          background-color: #ffffff !important;
        }
        tr:hover .pinned-cell {
          background-color: rgb(251,251,251) !important;
        }
        `}
      </style>
      <OverviewSectionWrapper
        title={title}
        titleIcon={IconTrophy}
        showInfoIcon={true}
        tooltipContent="See your top-performing creatives ranked by spends, your evergreen champions in one place."
        rightSideHeader={
          !loading && !error?.hasError && data && data.rows?.length > 0 ? (
            <EditMetricsToggle
              active={showAddMetric}
              onToggle={() => setShowAddMetric((i) => !i)}
            />
          ) : undefined
        }
      >
        {loading ? (
          <div className="w-full h-[300px] flex items-center justify-center">
            <WaveLoader />
          </div>
        ) : error?.hasError ? (
          <div className="w-full h-[300px] flex items-center justify-center">
            <ErrorState onRetry={onRetry} />
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="w-full h-[300px] flex items-center justify-center">
            <NothingToShow />
          </div>
        ) : (
          <>
            {showAddMetric && (
              <Metrics
                metricsOptions={metricOptions}
                selectedMetrics={selectedMetrics}
                onMetricChange={handleMetricsChange}
                className="mb-2"
              />
            )}
            <div className="max-w-full">
              <div className="w-full p-[1px] rounded-[12px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  className="rounded-[12px] overflow-x-auto"
                  onScroll={handleScroll}
                >
                  <Table
                    className="w-full rounded-[12px] border-separate border-spacing-0 cursor-default bg-white"
                    style={{
                      minWidth: "max-content",
                      tableLayout: "fixed",
                    }}
                  >
                    <TableHeader>
                      <TableRow>
                        {/* Dynamic columns from data */}
                        {data?.columns.map((column) => {
                          const textAlign = getTextAlignment(column);
                          const pinnedStyle = getPinnedColumnStyle(column, true);
                          const columnWidth = getColumnWidth(column);
                          const pinnedClasses = getPinnedColumnClasses(column);

                          return (
                            <TableHead
                              key={column.key}
                              className={cn(
                                "p-0 border-r border-b border-border-gray",
                                textAlign,
                                pinnedClasses,
                              )}
                              style={{
                                ...pinnedStyle,
                                width: columnWidth,
                                minWidth: columnWidth,
                              }}
                            >
                              <div
                                className={cn(
                                  "px-[20px] py-1 text-[13px] font-medium text-brand-primary-800 whitespace-nowrap",
                                  column.key === "creative_name" && "px-[12px]",
                                )}
                              >
                                {column.label}
                              </div>
                            </TableHead>
                          );
                        })}
                        <TableHead
                          className="p-0 border-r-0 border-b border-border-gray text-left"
                          style={{ maxWidth: "800px", width: "200px" }}
                        >
                          <div className="px-1 py-1 w-min">
                            <Metrics
                              selectedMetrics={selectedMetrics}
                              metricsOptions={metricOptions}
                              onMetricChange={handleMetricsChange}
                              renderingStyle={"multiselect"}
                              trigger={
                                <SqButton
                                  bgColor="white"
                                  size={"md"}
                                  hoverBgColor="#FBFBFB"
                                  hasOutline
                                  className="h-8 w-8 flex items-center justify-center"
                                >
                                  <RiAddLargeLine
                                    size={14}
                                    color="#9AA3BD"
                                    className="shrink-0"
                                  />
                                </SqButton>
                              }
                            />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentPageRows.length > 0 ? (
                        currentPageRows.map((row, rowIndex) => {
                          const actualRowIndex = startIndex + rowIndex; // Use actual row index for proper ranking
                          return (
                            <TableRow key={`row-${actualRowIndex}`} data-state="default">
                              {/* Dynamic columns from data */}
                              {data?.columns.map((column) => {
                                const textAlign = getTextAlignment(column);
                                const pinnedStyle = getPinnedColumnStyle(column, false);
                                const columnWidth = getColumnWidth(column);
                                const pinnedClasses = getPinnedColumnClasses(column);

                                return (
                                  <TableCell
                                    key={column.key}
                                    className={cn(
                                      "p-0 border-r border-border-gray",
                                      textAlign,
                                      pinnedClasses,
                                      rowIndex < pagination.pageSize - 1 &&
                                        rowIndex !== currentPageRows.length - 1
                                        ? "border-b"
                                        : "",
                                    )}
                                    style={{
                                      ...pinnedStyle,
                                      width: columnWidth,
                                      minWidth: columnWidth,
                                    }}
                                  >
                                    {renderCell(row, column, actualRowIndex)}
                                  </TableCell>
                                );
                              })}
                              {/* Empty column cell */}
                              <TableCell
                                className={`p-0 border-border-gray border-r-0  border-l-0 text-left ${rowIndex < pagination.pageSize - 1 && rowIndex !== currentPageRows.length - 1 ? "border-b" : ""}`}
                                style={{ maxWidth: "800px", width: "200px" }}
                              >
                                <div className="h-full">&nbsp;</div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={(data?.columns.length || 0) + 1}
                            className="h-24 text-center"
                          >
                            No results.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="w-full flex items-center justify-between text-brand-primary-new mt-[12px]">
                  <div className="flex items-center space-x-2">
                    <SelectDropdown
                      options={pageSizeOptions.map((pageSize) => ({
                        key: `${pageSize}`,
                        label: `${pageSize}`,
                        value: pageSize,
                      }))}
                      label="Creatives per page"
                      selected={pagination.pageSize}
                      onChange={(value) => handlePageSizeChange(value)}
                      className=""
                    />
                  </div>
                  <div className="flex items-center gap-[6px]">
                    <Button
                      variant="sq-outline"
                      className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
                      onClick={() => handlePageChange(pagination.pageIndex - 1)}
                      disabled={pagination.pageIndex === 0}
                    >
                      <span className="sr-only">Go to previous page</span>
                      <RiArrowLeftSLine className="h-4 w-4" color="#9aa3bd" />
                    </Button>

                    <AnimatePresence mode="popLayout">
                      {getVisiblePages(pagination.pageIndex + 1, totalPages).map(
                        (item, index) =>
                          item === "ellipsis" ? (
                            <motion.span
                              key={`ellipsis-${index}`}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15 }}
                              className="flex h-8 w-8 items-center justify-center text-[12px] text-[#35467A] select-none"
                            >
                              &hellip;
                            </motion.span>
                          ) : (
                            <motion.button
                              key={item}
                              layout
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15 }}
                              onClick={() => handlePageChange(item - 1)}
                              className={cn(
                                "relative h-8 w-8 rounded-[8px] text-[12px] transition-colors duration-200 ease-in-out",
                                item === pagination.pageIndex + 1
                                  ? "text-[#FF4500] font-medium"
                                  : "text-[#35467A] font-normal hover:bg-hover-gray",
                              )}
                            >
                              {item === pagination.pageIndex + 1 && (
                                <motion.div
                                  layoutId="leaderboard-active-page-bg"
                                  className="absolute inset-0 rounded-[8px] bg-[#FFECE4]"
                                  transition={{
                                    type: "spring",
                                    stiffness: 350,
                                    damping: 30,
                                  }}
                                />
                              )}
                              <span className="relative z-10">{item}</span>
                            </motion.button>
                          ),
                      )}
                    </AnimatePresence>

                    <Button
                      variant="sq-outline"
                      className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
                      onClick={() => handlePageChange(pagination.pageIndex + 1)}
                      disabled={pagination.pageIndex >= totalPages - 1}
                    >
                      <span className="sr-only">Go to next page</span>
                      <RiArrowRightSLine className="h-4 w-4" color="#9aa3bd" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Creative Details Dialog */}
            <CreativeDetailsDialog
              open={showDetailsDialog}
              onOpenChange={setShowDetailsDialog}
              columnData={data?.ad_columns}
              creative={selectedCreative}
              defaultMetricId={defaultMetricId}
              metricColumns={data?.creative_metric_columns}
              reportMetadata={reportMetadata}
            />
          </>
        )}
      </OverviewSectionWrapper>
    </>
  );
};

export default AllTimeCreativeLeaderboard;
